import testUiData from '../constants/test.json';
import personsData from '../constants/persons.json';
import locationsData from '../constants/locations.json';
import DirectionalRecord from './recordClasses/directionalRecord';
import DrillingRecord from './recordClasses/drillingRecord';
import AnalysisRecord from './recordClasses/analysisRecord';
import WellRecord from './recordClasses/wellRecord';

class BudgetItem {
  constructor(amount, date, description) {
    this.amount = amount;
    this.date = date;
    this.description = description;
  }
}

class DocumentDef {
  constructor(internalName, displayName, numPages) {
    this.fileExtension = 'jpg';

    this.internalName = internalName;
    this.displayName = displayName;
    this.numPages = numPages;
  }
}

class PersonsDef {
  constructor() {
    this.persons = personsData.persons;
  }
}

class Person {
  constructor(
    internalName,
    displayName,
    caption,
    useVideo,
    videoPrefix,
    answers,
  ) {
    this.internalName = internalName;
    this.displayName = displayName;
    this.caption = caption;
    this.useVideo = useVideo;
    this.videoPrefix = videoPrefix;
    this.answers = answers;
    this.documents = [];
  }

  addDocument(docName) {
    this.documents.push(docName);
  }
}

class Location {
  constructor(
    internalName,
    displayName,
    boundaries,
    description,
    person,
    sublocations,
  ) {
    this.internalName = internalName;
    this.displayName = displayName;
    this.boundaries = boundaries;
    this.description = description;
    this.person = person;
    this.sublocations = sublocations;
  }
}

class TestSyncObject {
  constructor(testName, x, y, z, cost, date, description, paramString) {
    this.testName = testName;
    this.x = x;
    this.y = y;
    this.z = z;
    this.cost = cost;
    this.date = date;
    this.description = description;
    this.paramString = paramString;
  }
}

class InfoSyncObject {
  constructor(infoType, internalName, cost, date, description, paramString) {
    this.infoType = infoType;
    this.internalName = internalName;
    this.cost = cost;
    this.date = date;
    this.description = description;
    this.paramString = paramString;
  }
}

class DataStore {
  constructor(syncQueue) {
    for (let key in testUiData) {
      this[key] = testUiData[key];
    }

    this.balance = 0;
    this.username = 'nobody';

    this.budgetString = '';
    this.budgetList = [];
    this.narrativeEnabled = true;
    this.superTestEnabled = false;

    this.documentsCBDP = [];

    this.syncQueue = syncQueue;
    this.documents = {};
    this.userDocuments = {};
    this.people = {};
    this.locations = {};

    for (let testName in this.testNames) {
      this[testName] = {};
    }

    const documents = [
      ['youtube', 'Videos: Phase 1 and Phase 2', 2],
      ['slCatalog', 'Self-Lume catalog', 9],
      ['slBrochure', 'Self-Lume brochure', 4],
      ['slMap', 'Self-Lume property map', 1],
      ['contract', 'Copy of contract text', 5],
      ['recon', 'Visual reconnaissance', 6],
      ['fireAlarm', 'Fire alarm report', 1],
      ['fireIncident', 'Fire incident report', 1],
      ['policeReport', 'Police report', 1],
      ['purchaseOrder', 'Purchase order', 1],
      ['sanitationReport', 'Sanitation report', 2],
      ['wellRecord', 'Well record', 2],
      ['siteWellData', 'Site well data', 4],
    ];

    this.docINames = documents.map(
      document => new DocumentDef(document[0], document[1], document[2]),
    );
    let i;
    for (i = 0; i < documents.length; i++) {
      this.documents[documents[i][0]] = new DocumentDef(
        documents[i][0],
        documents[i][1],
        documents[i][2],
      );
    }

    for (i = 0; i < 4; i++) {
      this.giveDocument(documents[i][0]);
    }

    const personsDef = new PersonsDef();
    for (i = 0; i < personsDef.persons.length; i++) {
      let person = personsDef.persons[i];
      this.people[person[0]] = new Person(
        person[0],
        person[1],
        person[2],
        person[3],
        person[4],
        person[5],
      );
    }

    this.people['anneSpector'].addDocument('sanitationReport');
    this.people['arthurRizer'].addDocument('purchaseOrder');
    this.people['bobBurnham'].addDocument('fireAlarm');
    this.people['bobBurnham'].addDocument('fireIncident');
    this.people['samKeystone'].addDocument('policeReport');
    this.people['mariNer'].addDocument('wellRecord');
    this.people['mariNer'].addDocument('siteWellData');

    for (i = 0; i < locationsData.locations.length; i++) {
      let location = locationsData.locations[i];
      let sublocations = null;
      if (location[5] !== null && typeof location[5] !== 'undefined') {
        sublocations = [];
        for (let j = 0; j < location[5].length; j++) {
          sublocations.push(this.locations[location[5][j]]);
        }
      }
      this.locations[location[0]] = new Location(
        location[0],
        location[1],
        location[2],
        location[3],
        this.people[location[4]],
        sublocations,
      );
    }
  }

  setParam(name, value) {
    this[name] = value;
  }

  savePerformedTest(testName, x, y, cost, obj, resultData) {
    let curDate = new Date();
    let description;
    let paramString = null;
    let z = null;
    switch (testName) {
      case 'analysis':
        description =
          'Performed ' +
          this.analysisInfo[obj.latestAnalysis].displayName +
          ' analysis';
        z = obj.latestElevation;
        paramString = obj.latestAnalysis;
        break;
      case 'drilling':
        description = obj.latestMethod !== 'drill' ? 'Direct push' : 'Drilling';
        z = obj.latestElevation;
        paramString = obj.latestMethod;
        break;
      case 'gpr':
        description = 'GPR';
        paramString = obj.latestDirection;
        break;
      case 'well':
        description = 'Installed well';
        z = obj.latestElevation;
        break;
      default:
        description = this.testNames[testName];
    }
    description += ' at ' + x + ', ' + y;

    if ((testName !== 'analysis' && testName !== 'drilling' && testName !== 'well') && resultData) {
      description += ': ' + resultData;
    } else {
      description += '.';
    }

    this.put(testName, x, y, obj);
    this.modifyBudget(cost, curDate, description);
    this.syncQueue.unshift(
      new TestSyncObject(
        testName,
        x,
        y,
        z,
        cost,
        curDate,
        description,
        paramString,
      ),
    );
    this.syncQueue.saveData();
  }

  saveReceivedDocument(docName) {
    if (!this.giveDocument(docName)) {
      return false;
    }

    var curDate = new Date();
    var description = 'Received ' + this.documents[docName].displayName + '.';
    this.modifyBudget(0, curDate, description);
    this.syncQueue.unshift(
      new InfoSyncObject('doc', docName, 0, curDate, description, null),
    );
    this.syncQueue.saveData();
  }

  saveQuestion(personName, cost) {
    var curDate = new Date();
    var displayName = this.people[personName].displayName;
    if (!displayName) {
      return;
    }
    var description = 'Questioned ' + displayName + '.';
    this.modifyBudget(cost, curDate, description);
    this.syncQueue.unshift(
      new InfoSyncObject(
        'question',
        personName,
        cost,
        curDate,
        description,
        null,
      ),
    );
    this.syncQueue.saveData();
  }

  saveVisit(locationName, cost) {
    let curDate = new Date();
    let displayName = this.locations[locationName].displayName;
    if (!displayName) {
      return;
    }
    var description = 'Visited ' + displayName + '.';
    this.modifyBudget(cost, curDate, description);
    this.syncQueue.unshift(
      new InfoSyncObject(
        'visit',
        locationName,
        cost,
        curDate,
        description,
        null,
      ),
    );
    this.syncQueue.saveData();
  }

  saveRecon(cost) {
    var curDate = new Date();
    var description = 'Performed visual reconnaissance.';
    this.modifyBudget(cost, curDate, description);
    this.syncQueue.unshift(
      new InfoSyncObject('recon', '', cost, curDate, description, null),
    );
    this.syncQueue.saveData();
  }

  getTestData(testName, x, y) {
    var data = this.get(testName, x, y);
    if (!data) {
      switch (testName) {
        case 'analysis':
          return new AnalysisRecord();
        case 'drilling':
          return new DrillingRecord();
        case 'gpr':
          return new DirectionalRecord();
        case 'well':
          return new WellRecord();
        default:
      }
    }
    return data;
  }

  loadReceivedDocument(docName) {
    if (!docName) {
      return false;
    }
    return this.giveDocument(docName);
  }

  loadPerformedTest(testName, x, y, z, paramString) {
    if (!testName) {
      return false;
    }
    var testData = true;
    switch (testName) {
      case 'analysis':
        testData = this.getTestData('analysis', x, y);
        testData.update(paramString, z);
        break;
      case 'drilling':
        testData = this.getTestData('drilling', x, y);
        testData.update(z, paramString);
        break;
      case 'gpr':
        testData = this.getTestData('gpr', x, y);
        testData.update(paramString);
        break;
      case 'well':
        testData = this.getTestData('well', x, y);
        testData.update(z);
        break;
      default:
    }
    this.put(testName, x, y, testData);
    return true;
  }

  loadBudgetItem(cost, date, description) {
    var item = new BudgetItem(cost, date, description);
    this.budgetList.push(item);
    this.updateBudgetString(item);
  }

  loadBalance(balance) {
    this.balance = balance;
  }

  giveDocument(docName) {
    if (typeof this.documents[docName] === 'undefined') {
      return false;
    }
    if (this.userDocuments[docName]) {
      return false;
    }
    this.userDocuments[docName] = true;
    this.documentsCBDP.push({
      title: this.documents[docName].displayName,
      data: docName,
    });
    return true;
  }

  updateBudgetString(item) {
    this.budgetString +=
      '<div><div>$' +
      item.amount +
      '</div><div>' +
      (item.date.getMonth() + 1) +
      '/' +
      item.date.getDate() +
      '/' +
      item.date.getFullYear() +
      '</div><div>  ' +
      item.description +
      '</div></div>';
  }

  modifyBudget(amount, date, description) {
    var item = new BudgetItem(amount, date, description);
    this.budgetList.push(item);
    this.updateBudgetString(item);
    this.balance += amount;
  }

  get(testName, x, y) {
    if (typeof this[testName][String(x)] === 'undefined') {
      return undefined;
    } else {
      return this[testName][String(x)][String(y)];
    }
  }

  put(testName, x, y, obj) {
    const xVal = String(x);
    const yVal = String(y);
    if (typeof this[testName][xVal] === 'undefined') {
      this[testName][xVal] = {};
    }
    this[testName][xVal][yVal] = obj;
    return !this[testName][xVal][yVal];
  }

  getTestsByName(testName) {
    let result = [];
    for (let x in this[testName]) {
      for (let y in this[testName][x]) {
        result.push({ x, y, value: this[testName][x][y] });
      }
    }
    return result;
  }

  getTestsAmountByName(testName) {
    let result = 0;
    for (let x in this[testName]) {
      for (let y in this[testName][x]) {
        result++;
      }
    }
    return result;
  }
}

export default DataStore;
